import NewIcon from '@/assets/icons/new.png';
import { Logger, Sound } from '@/classes';
import { keyCodes } from '@/constants';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { useRouter } from '@/hooks/useRouter';
import { Expose } from '@/shared/ui';
import { ContentGroup } from '@/types';
import { focusNeighborInView } from '@/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ContentGroupCardProps = {
  contentGroup: ContentGroup;
  focusKey?: string;
  onFocus?: ({ x, y, left, top }: { x: number; y: number; left: number; top: number }) => void;
  containerId?: number;
  weight: number;
};

export const ContentGroupCard = ({ contentGroup, focusKey, onFocus, containerId, weight }: ContentGroupCardProps) => {
  const [searchParams] = useSearchParams();
  const { ref, focused, focusSelf } = useExtendFocusable({
    focusKey,
    onFocus,
    // 방향키 이동은 콘텐츠 좌표가 아니라 화면에 보이는 좌표를 기준으로 한다.
    onArrowPress: (direction) => focusNeighborInView(direction),
  });
  const router = useRouter();

  const onClick = useCallback(() => {
    Logger.clickTopic(contentGroup.id, weight, containerId);
    Sound.press();
    router.push(`/topics/${contentGroup.id}`, { focusKey });
  }, [containerId, contentGroup.id, focusKey, router, weight]);

  useFocusElement({ ref, focused, onClick, keyCode: keyCodes.ENTER });

  useEffect(() => {
    const targetFocusKey = searchParams.get('f');
    if (focusKey === targetFocusKey) {
      focusSelf();
    }
  }, [focusKey, focusSelf, searchParams]);

  const alreadyExposedRef = useRef(false);
  const handleExposed = useCallback(() => {
    if (!alreadyExposedRef.current) {
      Logger.exposedTopic(contentGroup.id, weight, containerId);
      alreadyExposedRef.current = true;
    }
  }, [containerId, contentGroup.id, weight]);

  return (
    <Expose
      className="w-[312px] h-[426px] mr-[24px] mb-[4px] flex flex-col items-center justify-center relative"
      onExposed={handleExposed}
      threshold={Number.EPSILON}
    >
      {/* 썸네일 */}
      <img
        tabIndex={0}
        src={contentGroup.thumbnail.src}
        ref={ref}
        onClick={onClick}
        onContextMenu={onClick}
        className="rounded-full w-[280px] h-[280px] mx-[16px] mt-[20px] outline-disable outline-primary-12 ease-in-out duration-300 bg-gray-400"
      />

      {/* 신규 콘텐츠 스티커 */}
      {contentGroup.sticker === 'NEW' && (
        <img src={NewIcon} className="absolute top-[20px] left-0 w-[110px] h-[51px]" />
      )}

      <div
        tabIndex={0}
        className="flex w-[292px] h-[80px] mt-[20px] mb-[26px] mx-[10px] font-bold text-center text-[34px] leading-[39.6px]"
        style={{ color: focused ? '#FF66AE' : '#2B2B2B' }}
      >
        <span className="m-auto">{contentGroup.title}</span>
      </div>
    </Expose>
  );
};
